import React, { useRef, useState, useEffect } from 'react';
import { Download, RotateCcw, Share2, Check, Sparkles, X, ShieldAlert, FileText, Link2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { QuizResult } from '../types';
import { ResultCard } from './ResultCard';
import { ShareCard, SHARE_CARD_WIDTH, SHARE_CARD_HEIGHT, SHARE_QR_TARGET_URL } from './ShareCard';

// 复制文案 / 系统分享中携带的正式测试网址（与分享卡二维码同源，保持永远一致）
const SHARE_TEST_URL = SHARE_QR_TARGET_URL;
import { CHARACTER_MAP, CharacterDefinition } from '../data/characterRegistry';
import { MatchResult, generateResultForCandidate } from '../utils/scoringEngine';

interface ResultViewProps {
  initialResult: QuizResult;
  matchResult?: MatchResult | null;
  onRestart: () => void;
}

export const ResultView: React.FC<ResultViewProps> = ({
  initialResult,
  matchResult,
  onRestart,
}) => {
  const [currentResult, setCurrentResult] = useState<QuizResult>(initialResult);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isGeneratingImg, setIsGeneratingImg] = useState(false);
  const [exportedImgUrl, setExportedImgUrl] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [copiedToast, setCopiedToast] = useState(false);
  const [copiedLinkToast, setCopiedLinkToast] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const shareCardRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  // 检测当前浏览器是否支持 Web Share API 的文件分享（iOS Safari / Android Chrome 支持；
  // 微信/小红书内置浏览器大多不支持，会回退到「长按图片保存」路径）
  const canShareFiles: boolean =
    typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({
      files: [new File([new Blob(['x'])], 'probe.png', { type: 'image/png' })],
    });

  // 触屏设备（微信内置浏览器等不支持文件分享时，主路径为长按图片保存）
  const isTouchDevice: boolean =
    typeof window !== 'undefined' &&
    (window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0);

  // 动态等比缩放比例：根据手机/屏幕容器宽度自动计算 scale = availableWidth / 卡片宽度
  const [previewScale, setPreviewScale] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      return Math.min(1, Math.max(0.1, (window.innerWidth - 48) / SHARE_CARD_WIDTH));
    }
    return 0.4;
  });

  useEffect(() => {
    if (!isPreviewOpen) return;

    const updateScale = () => {
      if (previewContainerRef.current) {
        const containerWidth = previewContainerRef.current.clientWidth;
        if (containerWidth > 0) {
          // 减去 8px 边距安全距离，确保两边完全不顶格、绝不产生裁切与横向滚动
          const computedScale = Math.min(1, Math.max(0.1, (containerWidth - 8) / SHARE_CARD_WIDTH));
          setPreviewScale(computedScale);
        }
      }
    };

    updateScale();
    const ro = new ResizeObserver(updateScale);
    if (previewContainerRef.current) {
      ro.observe(previewContainerRef.current);
    }
    window.addEventListener('resize', updateScale);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, [isPreviewOpen]);

  // 始终动态映射当前角色的正式定义，获取精准的 character.image
  const currentCharacter: CharacterDefinition =
    CHARACTER_MAP[currentResult.id] || {
      id: currentResult.id,
      name: currentResult.targetName,
      pool: currentResult.badgeType === 'danger' ? 'abstract' : 'real',
      personaKeywords: [],
      image: currentResult.imageUrl,
      coordinates: { C: 5, E: 4, I: 1, S: 2, M: 3, P: 4, V: 4, N: 1, X: 2, A: 1, B: 1, R: 1 },
    };

  // 生成/导出适合小红书与朋友圈的高清独立分享海报图
  // 导出目标严格限定为独立 ShareCard DOM 节点本身，固定 900×1350，
  // 绝不截取页面、可视区域或任何网页 UI（按钮/弹窗外框均不在导出内容内）
  const handleExportPng = async () => {
    const el = shareCardRef.current;
    if (!el) {
      setExportError('分享卡尚未就绪，请关闭弹窗后重新打开再试。');
      return;
    }
    setIsGeneratingImg(true);
    setExportError(null);
    try {
      // 1. 等待字体加载完成
      if (document.fonts) {
        await document.fonts.ready;
      }

      // 2. 检查并确保 ShareCard 内所有图片完全解码
      const images = Array.from(el.querySelectorAll('img'));
      await Promise.all(
        images.map(async (img) => {
          if (!img.complete || img.naturalWidth === 0) {
            await new Promise<void>((resolve) => {
              const timer = setTimeout(() => resolve(), 3000);
              img.onload = () => {
                clearTimeout(timer);
                resolve();
              };
              img.onerror = () => {
                clearTimeout(timer);
                resolve();
              };
            });
          }
          if ('decode' in img) {
            try {
              await img.decode();
            } catch (e) {
              console.warn('Image decode error:', e);
            }
          }
        })
      );

      // 3. toPng 只渲染 ShareCard 节点，按正式卡片规格固定导出高清 PNG
      const exportOnce = () =>
        toPng(el, {
          cacheBust: false,
          pixelRatio: 1,
          backgroundColor: '#FAF7F0',
          width: SHARE_CARD_WIDTH,
          height: SHARE_CARD_HEIGHT,
        });

      let dataUrl: string;
      try {
        dataUrl = await exportOnce();
      } catch {
        // 部分移动端浏览器首次渲染 foreignObject 可能失败，自动原样重试一次
        dataUrl = await exportOnce();
      }

      setExportedImgUrl(dataUrl);
      if (typeof window !== 'undefined') {
        (window as any).__LAST_EXPORTED_IMAGE__ = dataUrl;
      }

      // 4. 仅在不支持文件分享的环境（如桌面端）自动触发 PNG 下载；
      //    支持系统分享面板的移动端由用户点击「保存/分享」按钮触发，避免双重动作
      if (!canShareFiles) {
        const link = document.createElement('a');
        link.download = `伴侣物种裁决_${currentResult.targetName.split(' ')[0]}_分享卡.png`;
        link.href = dataUrl;
        link.click();
      }
    } catch (err) {
      console.error('Failed to generate poster', err);
      setExportError('导出失败，请点击上方导出按钮重试一次。');
    } finally {
      setIsGeneratingImg(false);
    }
  };

  const handleCopyShareText = () => {
    const shareText = `【测测你这辈子到底适合养什么东西】\n我的系统裁决结果是：${currentResult.targetName}（${currentResult.subtitle}）\n综合匹配度：${currentResult.matchRate}\n快来测测你这辈子被分配了什么不可名状之物……\n你也来测测：${SHARE_TEST_URL}`;
    navigator.clipboard.writeText(shareText);
    setCopiedToast(true);
    setTimeout(() => setCopiedToast(false), 2000);
  };

  // 独立复制测试链接（只复制网址本身）
  const handleCopyLink = () => {
    navigator.clipboard.writeText(SHARE_TEST_URL);
    setCopiedLinkToast(true);
    setTimeout(() => setCopiedLinkToast(false), 2000);
  };

  // 唤起系统原生分享面板（iOS Safari / Android Chrome 等支持文件分享的浏览器）：
  // 面板内可「存储图像」到相册，或直接分享到微信 / 小红书等 App；分享文本携带测试网址
  const handleShareImage = async () => {
    if (!exportedImgUrl) return;
    try {
      const blob = await (await fetch(exportedImgUrl)).blob();
      const file = new File(
        [blob],
        `伴侣物种裁决_${currentResult.targetName.split(' ')[0]}_分享卡.png`,
        { type: 'image/png' }
      );
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: '测测你这辈子到底适合养什么东西',
          text: `我的系统裁决结果是「${currentResult.targetName}」，快来测测你适合养什么→\n你也来测测：${SHARE_TEST_URL}`,
        });
      }
    } catch (err) {
      // 用户取消分享（AbortError）或面板唤起失败时静默降级，保留长按保存路径
      console.warn('Share failed:', err);
    }
  };

  return (
    <div className="min-h-[100dvh] px-4 py-5 max-w-md mx-auto space-y-4 animate-fade-in relative pb-12 bg-paper-100">
      {/* 顶部候选切换栏：若有正式答题匹配结果，展示判定池与 Top 3 候选 */}
      {matchResult && matchResult.top3 && matchResult.top3.length > 0 && (
        <div className="p-3 bg-white border-2 border-ink-950 shadow-sharp-sm space-y-2">
          <div className="flex items-center justify-between text-xs font-mono font-bold text-ink-900">
            <span className="flex items-center gap-1">
              <FileText
                className={`w-3.5 h-3.5 ${
                  matchResult.routing.pool === 'abstract' ? 'text-stamp' : 'text-lab-green'
                }`}
              />
              <span>
                系统判定: {matchResult.routing.pool === 'abstract' ? '【抽象生物池】' : '【现实宠物池】'}
                {matchResult.routing.isAbstractOverride && ' (越权)'}
              </span>
            </span>
            <span className="text-[10px] text-ink-500 font-normal">
              风险 G={matchResult.routing.realityRisk}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-1.5 font-mono">
            {matchResult.top3.map((cand, idx) => {
              const isSelected = currentResult.id === cand.character.id;
              return (
                <button
                  key={cand.character.id}
                  onClick={() => {
                    const card = generateResultForCandidate(
                      cand,
                      matchResult.routing,
                      matchResult.userScores12D
                    );
                    setCurrentResult(card);
                  }}
                  type="button"
                  className={`py-1.5 px-1 text-[11px] font-bold border transition-all cursor-pointer text-center truncate ${
                    isSelected
                      ? matchResult.routing.pool === 'abstract'
                        ? 'bg-stamp text-paper-50 border-stamp shadow-sharp-sm'
                        : 'bg-ink-900 text-paper-50 border-ink-950 shadow-sharp-sm'
                      : 'bg-paper-50 text-ink-800 border-ink-400 hover:bg-paper-200'
                  }`}
                >
                  {idx === 0 ? '★ ' : `${idx + 1}. `}
                  {cand.character.name} {cand.matchRate}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 核心卡片容器（完整网页结果卡：保留完整综合判定长文与详细分析） */}
      <div className="animate-scale-in">
        <ResultCard ref={cardRef} result={currentResult} />
      </div>

      {/* 底部操作区 */}
      <div className="space-y-2.5 pt-2">
        <button
          onClick={() => {
            setExportedImgUrl(null);
            setExportError(null);
            setIsPreviewOpen(true);
          }}
          type="button"
          className="w-full py-3.5 px-5 bg-ink-950 hover:bg-ink-900 active:translate-x-0.5 active:translate-y-0.5 text-paper-50 font-black text-sm shadow-sharp border-2 border-ink-950 transition-all flex items-center justify-center gap-2 cursor-pointer"
        >
          <Download className="w-4 h-4" />
          <span>查看并生成社交分享结果卡（发小红书/朋友圈）</span>
        </button>

        <div className="grid grid-cols-3 gap-2 font-mono">
          <button
            onClick={handleCopyShareText}
            type="button"
            className="py-3 px-2 bg-white border-2 border-ink-900 hover:bg-paper-100 text-ink-900 font-bold text-xs shadow-sharp-sm transition-all flex items-center justify-center gap-1 cursor-pointer"
          >
            {copiedToast ? (
              <>
                <Check className="w-3.5 h-3.5 text-lab-green" />
                <span className="text-lab-green">文案已复制</span>
              </>
            ) : (
              <>
                <Share2 className="w-3.5 h-3.5" />
                <span>复制文案</span>
              </>
            )}
          </button>

          <button
            onClick={handleCopyLink}
            type="button"
            className="py-3 px-2 bg-white border-2 border-ink-900 hover:bg-paper-100 text-ink-900 font-bold text-xs shadow-sharp-sm transition-all flex items-center justify-center gap-1 cursor-pointer"
          >
            {copiedLinkToast ? (
              <>
                <Check className="w-3.5 h-3.5 text-lab-green" />
                <span className="text-lab-green">链接已复制</span>
              </>
            ) : (
              <>
                <Link2 className="w-3.5 h-3.5" />
                <span>复制链接</span>
              </>
            )}
          </button>

          <button
            onClick={onRestart}
            type="button"
            className="py-3 px-2 bg-white border-2 border-ink-900 hover:bg-paper-100 text-ink-900 font-bold text-xs shadow-sharp-sm transition-all flex items-center justify-center gap-1 cursor-pointer"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>重新开始</span>
          </button>
        </div>

        <p className="text-center text-[10px] font-mono text-ink-400">
          💡 网页版保留完整长文与详细分析；点击上方按钮可生成适合发小红书的精简分享卡
        </p>
      </div>

      {/* 社交分享卡实时预览与导出弹窗 */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-50 bg-ink-950/85 backdrop-blur-xs flex flex-col items-center justify-center p-3 sm:p-4 animate-fade-in">
          <div className="bg-paper-50 border-3 border-ink-950 rounded-xs p-3 sm:p-4 max-w-xl w-full max-h-[96vh] flex flex-col items-center overflow-y-auto space-y-3 shadow-2xl relative">
            <button
              onClick={() => {
                setIsPreviewOpen(false);
                setExportedImgUrl(null);
                setExportError(null);
              }}
              className="absolute top-3 right-3 w-8 h-8 bg-paper-200 border-2 border-ink-900 flex items-center justify-center text-ink-900 hover:bg-paper-300 cursor-pointer font-mono z-30"
              aria-label="关闭预览"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="text-center pt-1 w-full border-b border-ink-200 pb-2">
              <h3 className="text-xs font-mono font-black uppercase text-ink-900">
                // 社交分享卡 · 实时档案预览 //
              </h3>
              <p className="text-[10px] text-ink-500 font-mono mt-0.5">
                精炼 8 维指标与物种判定 · 点击下方按钮直接导出高清 PNG
              </p>
            </div>

            {/* 真实 DOM 预览视窗：动态等比缩放，绝不裁切 */}
            <div
              ref={previewContainerRef}
              className="w-full flex justify-center py-1 overflow-visible"
            >
              <div
                style={{
                  width: `${SHARE_CARD_WIDTH * previewScale}px`,
                  height: `${SHARE_CARD_HEIGHT * previewScale}px`,
                  position: 'relative',
                }}
                className="shrink-0"
              >
                <div
                  style={{
                    width: `${SHARE_CARD_WIDTH}px`,
                    height: `${SHARE_CARD_HEIGHT}px`,
                    transform: `scale(${previewScale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <ShareCard
                    ref={shareCardRef}
                    character={currentCharacter}
                    userProfile={matchResult?.userScores12D}
                    metrics={currentResult.metrics}
                    matchRate={currentResult.matchRate}
                    caseId={currentResult.caseId}
                    subtitle={currentResult.subtitle}
                  />
                </div>
              </div>
            </div>

            {/* 导出操作区 */}
            <div className="w-full space-y-2 pt-1 border-t border-ink-200">
              <button
                onClick={handleExportPng}
                disabled={isGeneratingImg}
                type="button"
                className="w-full py-3 bg-ink-950 hover:bg-ink-900 active:translate-x-0.5 active:translate-y-0.5 text-paper-50 font-mono text-xs font-bold text-center flex items-center justify-center gap-2 shadow-sharp-sm transition-all cursor-pointer disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                <span>
                  {isGeneratingImg
                    ? `正在导出 ${SHARE_CARD_WIDTH}×${SHARE_CARD_HEIGHT} 高清 PNG...`
                    : `导出并下载高清分享卡 (${SHARE_CARD_WIDTH}×${SHARE_CARD_HEIGHT} PNG)`}
                </span>
              </button>

              {exportedImgUrl && (
                <div className="space-y-2 p-2.5 bg-paper-100 border-2 border-dashed border-ink-400">
                  {canShareFiles ? (
                    <>
                      {/* 支持文件分享：唤起系统原生分享面板（含保存到相册 / 分享到 App） */}
                      <button
                        onClick={handleShareImage}
                        type="button"
                        className="w-full py-3 bg-stamp hover:bg-stamp-dark text-paper-50 font-mono text-xs font-bold text-center flex items-center justify-center gap-2 shadow-sharp-sm border-2 border-stamp transition-all active:translate-x-0.5 active:translate-y-0.5 cursor-pointer"
                      >
                        <Share2 className="w-4 h-4" />
                        <span>保存图片到手机 / 分享给朋友</span>
                      </button>
                      <p className="text-[10px] font-mono text-ink-500 text-center">
                        点击后选择「存储图像」存入相册，或直接分享到微信 / 小红书
                      </p>
                    </>
                  ) : isTouchDevice ? (
                    /* 触屏但不支持文件分享（微信内置浏览器等）：长按成图保存 */
                    <div className="text-[11px] font-mono font-bold text-lab-green flex items-center justify-center gap-1">
                      <Check className="w-3.5 h-3.5" />
                      <span>结果图已生成，长按下方图片保存到相册</span>
                    </div>
                  ) : (
                    /* 桌面端：已触发 PNG 下载 */
                    <div className="text-[11px] font-mono font-bold text-lab-green flex items-center justify-center gap-1">
                      <Check className="w-3.5 h-3.5" />
                      <span>高清 PNG 已生成并已触发下载（{SHARE_CARD_WIDTH}×{SHARE_CARD_HEIGHT}）</span>
                    </div>
                  )}

                  {/* 完整成图预览：所有环境统一提供长按保存能力 */}
                  <img
                    src={exportedImgUrl}
                    alt="分享卡导出成图预览"
                    className="block w-full border border-ink-300 select-none"
                    draggable={false}
                  />

                  {!canShareFiles && (
                    <a
                      id="share-card-download-link"
                      href={exportedImgUrl}
                      download={`伴侣物种裁决_${currentResult.targetName.split(' ')[0]}_分享卡.png`}
                      className="inline-block w-full py-2 bg-white border border-ink-800 text-[10px] font-mono font-bold hover:bg-paper-200 text-ink-900 text-center"
                    >
                      {isTouchDevice ? '长按不便时可点此直接下载图片' : `若未自动下载，点此手动保存 PNG`}
                    </a>
                  )}
                  <p className="text-[9px] font-mono text-ink-500 text-center">
                    导出的 PNG 仅包含分享卡本体，不含任何网页界面与按钮
                  </p>
                </div>
              )}

              {exportError && (
                <div className="p-2.5 bg-stamp-light/60 border-2 border-stamp/70 text-center">
                  <div className="text-[11px] font-mono font-bold text-stamp-dark">
                    {exportError}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
